import { create } from "zustand";
import type { IAsset } from "@api/IWalletController";
import { initialAssets } from "./assets.store";
import { WalletController } from "@api/WalletController";
import { useWallet } from "./wallet.store";
import { ICreateSniperPayload, ICreateSniperResponse } from "@api/IWalletController";
import { showToast } from "@utils/showToast";

interface SniperStateData {
    snipeAssetInfo: {
        asset: IAsset | null;
        amount: string;
        balance: number;
        price: number | null;
    };
    sniperSpeed: string;
    snipeTargetAddress: string;
    autobuy: boolean;
    autosell: boolean;
    autoBuyAmount: string;
    autoSellAmount: string;
    memePad: boolean;
    dex: string;
}

interface SniperStateActions {
    setSnipeAsset: (asset: IAsset | null) => void;
    setSniperAmount: (amount: string) => void;
    setSniperSpeed: (speed: string) => void;
    setSnipeTargetAddress: (address: string) => void;
    setAutobuy: (autobuy: boolean) => void;
    setAutosell: (autosell: boolean) => void;
    setAutoBuyAmount: (value: number) => void;
    setAutoSellAmount: (value: number) => void;
    setMemePad: (memePad: boolean) => void;
    createSniper: () => Promise<ICreateSniperResponse>;
    updateSnipeAssetBalance: () => Promise<void>;
    setDex: (dex: string) => void;
}

type SniperState = SniperStateData & SniperStateActions;

const initialSniperStateData: SniperStateData = {
    snipeAssetInfo: {
        asset: initialAssets?.length > 1 ? initialAssets[0] : (initialAssets?.[0] ?? null),
        amount: "",
        balance: 0,
        price: null,
    },
    sniperSpeed: "normal",
    snipeTargetAddress: "",
    autobuy: false,
    autosell: false,
    autoBuyAmount: "",
    autoSellAmount: "",
    memePad: false,
    dex: "ston.fi_v1",
};

export const useSniper = create<SniperState>((set, get) => ({
    ...initialSniperStateData,

    setSnipeAsset: (asset: IAsset | null) => {
        set((state) => ({
            snipeAssetInfo: {
                ...state.snipeAssetInfo,
                asset,
                amount: "",
                price: null,
                balance: 0, // Reset balance when asset changes
            },
        }));
        // Optionally trigger balance update when asset is set
        get().updateSnipeAssetBalance();
    },

    setSniperAmount: (amount: string) => {
        set((state) => ({
            snipeAssetInfo: { ...state.snipeAssetInfo, amount },
        }));
    },

    setSniperSpeed: (speed: string) => {
        set(() => ({ sniperSpeed: speed }));
    },

    setSnipeTargetAddress: (address: string) => {
        set(() => ({ snipeTargetAddress: address }));
    },

    setAutobuy: (autobuy: boolean) => {
        set(() => ({ autobuy }));
    },

    setAutosell: (autosell: boolean) => {
        set(() => ({ autosell }));
    },

    setAutoBuyAmount: (value: number) => {
        set(() => ({ autoBuyAmount: value.toString() }));
    },

    setAutoSellAmount: (value: number) => {
        set(() => ({ autoSellAmount: value.toString() }));
    },

    setMemePad: (memePad: boolean) => {
        set(() => ({ memePad: memePad }));
    },

    setDex: (dex: string) => {
        set(() => ({ dex }));
    },

    createSniper: async () => {
        const state = get();
        const { currentWallet } = useWallet.getState();
        const {
            snipeAssetInfo,
            sniperSpeed,
            snipeTargetAddress,
            autosell,
            autoSellAmount,
            autobuy,
            autoBuyAmount,
            memePad,
            dex,
        } = state;
        const payload: ICreateSniperPayload = {
            walletGuid: currentWallet.guid,
            assetInGuid: snipeAssetInfo.asset?.guid || "",
            snipeAddress: snipeTargetAddress,
            autoBuy: autobuy ? autoBuyAmount : "0",
            autoSell: autosell ? autoSellAmount : "0",
            amount: snipeAssetInfo.amount,
            mode: sniperSpeed,
            dexes: dex === "all" ? ["ston.fi_v1, ston.fi_v2, dedust"] : dex.includes(",") ? dex.split(",") : [dex],
            dex: dex,
        };
        let apiResponse;
        if (memePad) {
            apiResponse = await WalletController.createMemePadSniper(payload);
        } else {
            apiResponse = await WalletController.createSniper(payload);
        }

        if (apiResponse.status !== "success") {
            showToast(apiResponse.message, "error");
        }
        const result = apiResponse.data;
        return result;
    },

    updateSnipeAssetBalance: async () => {
        const { snipeAssetInfo } = get();
        const { currentWallet } = useWallet.getState();

        if (!snipeAssetInfo.asset || !currentWallet?.guid) {
            set((state) => ({
                snipeAssetInfo: { ...state.snipeAssetInfo, balance: 0 },
            }));
            return;
        }

        try {
            const response = await WalletController.getBalances(currentWallet.guid, [snipeAssetInfo.asset]);

            if (response.status === "success" && response.data.length > 0) {
                const assetBalanceData = response.data[0];

                if (assetBalanceData.balances && Array.isArray(assetBalanceData.balances)) {
                    // Find balance for the snipe asset
                    const snipeAssetBalanceItem = assetBalanceData.balances.find(
                        (b) => b.jettonMasterAddress === snipeAssetInfo.asset?.jettonMasterAddress,
                    );

                    const newBalance = snipeAssetBalanceItem?.amount || 0;

                    set((state) => ({
                        snipeAssetInfo: {
                            ...state.snipeAssetInfo,
                            balance: newBalance,
                        },
                    }));
                } else {
                    set((state) => ({
                        snipeAssetInfo: { ...state.snipeAssetInfo, balance: 0 },
                    }));
                }
            } else {
                set((state) => ({
                    snipeAssetInfo: { ...state.snipeAssetInfo, balance: 0 },
                }));
            }
        } catch (error) {
            set((state) => ({
                // Reset balance on error
                snipeAssetInfo: { ...state.snipeAssetInfo, balance: 0 },
            }));
        }
    },
}));

// Subscribe to wallet changes to update balance
useWallet.subscribe((state, prevState) => {
    if (state.currentWallet?.guid !== prevState.currentWallet?.guid) {
        useSniper.getState().updateSnipeAssetBalance();
    }
});
